import { myFirebase } from '@/firebaseConfig/firebaseConfig';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import firebase from 'firebase/compat/app';
import { collection, query, where, onSnapshot, orderBy, getDoc, doc, Unsubscribe, getDocs } from 'firebase/firestore';

import { toast } from '@/components/Toaster/Toast';

import { Session, ReportInfoDict, SessionCreationData, SessionStatus, ReportInfo } from '@/models';

type Callback = (sessions: Session[], error?: Error) => void;

class ClientSessionManager {
  private firestore = myFirebase.firestore();
  private latestSessions: Session[] = [];
  private reportInfoDict: ReportInfoDict = {};
  private callbacks: Set<Callback> = new Set();
  private sessionsUnsubscribe?: Unsubscribe;

  private emitAll() {
    const enriched = this.enrichSessionsWithReportDict(this.latestSessions, this.reportInfoDict);
    for (const cb of this.callbacks) {
      try {
        cb(enriched);
      } catch (e) {
        console.error('emitAll callback error:', e);
      }
    }
  }

  private emitError(error: Error) {
    for (const cb of this.callbacks) {
      try {
        cb([], error);
        toast({
          variant: 'error',
          title: 'Error fetching sessions',
          description: error.message,
        });
      } catch (e) {
        console.error('emitError callback error:', e);
      }
    }
  }

  private enrichSessionsWithReportDict = (sessions: Session[], dict: ReportInfoDict): Session[] => {
    return sessions.map((session) => ({
      ...session,
      reportInfo: (session.reports || []).map((rid) => dict[rid]).filter(Boolean) as ReportInfo[],
    }));
  };

  public updateReportInfoDict(dict: ReportInfoDict) {
    this.reportInfoDict = dict ?? {};
    this.emitAll();
  }

  public getSessions = (userUID: string, clientID: string, _organizationId: string, callback: Callback) => {
    this.callbacks.add(callback);

    // Only one sessions snapshot at a time per manager instance.
    if (this.sessionsUnsubscribe) {
      this.sessionsUnsubscribe();
      this.sessionsUnsubscribe = undefined;
    }

    const sessionsRef = collection(this.firestore, 'sessions');
    const q = query(
      sessionsRef,
      where('therapistId', '==', userUID),
      where('status', '!=', SessionStatus.Archived),
      where('clientId', '==', clientID),
      orderBy('date', 'desc'),
    );

    this.sessionsUnsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          this.latestSessions = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Session[];
          // Add a filter to remove empty sessions (nor reports field or empty reports array)
          this.latestSessions = this.latestSessions.filter(
            (session) => Array.isArray(session.reports) && session.reports.length > 0,
          );
          this.emitAll();
        } catch (error) {
          console.error('Error handling sessions snapshot:', error);
          this.emitError(error instanceof Error ? error : new Error('Unknown error occurred'));
        }
      },
      (error) => {
        console.error('Snapshot listener error:', error);
        this.emitError(error);
      },
    );

    // Emit immediately with whatever we have
    this.emitAll();

    // Cleanup for the caller: remove the callback; if no more callbacks, stop the snapshot
    return () => {
      this.callbacks.delete(callback);
      if (this.callbacks.size === 0 && this.sessionsUnsubscribe) {
        this.sessionsUnsubscribe();
        this.sessionsUnsubscribe = undefined;
      }
    };
  };

  public async createSession(sessionData: SessionCreationData): Promise<string> {
    try {
      const sessionRef = this.firestore.collection('sessions').doc();
      const session = {
        ...sessionData,
        id: sessionRef.id,
        status: SessionStatus.Active,
        _mutationHistory: [
          {
            mutationType: 'create',
            mutationDate: firebase.firestore.Timestamp.fromDate(new Date()),
            mutationBy: sessionData.therapistId,
          },
        ],
      };

      await sessionRef.set(session);
      return sessionRef.id;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  // Remove session by setting its status to 'archived'
  public async archiveSession(sessionId: string, userId: string): Promise<string> {
    try {
      const sessionRef = this.firestore.collection('sessions').doc(sessionId);
      const sessionSnap = await getDoc(sessionRef);

      if (!sessionSnap.exists()) {
        throw new Error('Session not found');
      }

      await sessionRef.update({
        status: SessionStatus.Archived,
        _mutationHistory: firebase.firestore.FieldValue.arrayUnion({
          mutationType: 'archive',
          mutationDate: firebase.firestore.Timestamp.fromDate(new Date()),
          mutationBy: userId,
        }),
      });
      return 'success';
    } catch (error) {
      console.error('Error archiving session:', error);
      const errorMessage = `Error archiving session with id ${sessionId}, with error Message: ${
        error instanceof Error ? error.message : String(error)
      }`;
      return errorMessage;
    }
  }

  // Check if a report is already assigned to any session of the user and return the clientId and SessionId if so
  public async checkAlreadyAssigned(
    userUID: string,
    reportId: string,
  ): Promise<{ clientId: string; sessionId: string } | null> {
    try {
      const sessionsRef = collection(this.firestore, 'sessions');
      const q = query(sessionsRef, where('therapistId', '==', userUID), where('reports', 'array-contains', reportId));
      const querySnapshot = await getDocs(q);

      const matchingDoc = querySnapshot.docs.find((doc) => {
        const data = doc.data();
        return (
          data.status !== SessionStatus.Archived && typeof data.clientId === 'string' && data.clientId.trim() !== ''
        );
      });

      if (!matchingDoc) return null;

      const data = matchingDoc.data();
      return {
        clientId: data.clientId,
        sessionId: matchingDoc.id,
      };
    } catch (error) {
      console.error('Error checking assigned sessions:', error);
      throw error;
    }
  }
}

// eslint-disable-next-line import/no-default-export
export default new ClientSessionManager();
